import { createServerFn } from "@tanstack/react-start";
import { queryOptions } from "@tanstack/react-query";
import { z } from "zod";

// Öffentlicher TTPA-Lesepfad – bewusst getrennt von ttpa.functions.ts:
// jenes Modul importiert die Supabase-Auth-Middleware und würde supabase-js
// in das Einstiegs-Bundle jeder öffentlichen Seite ziehen.
const SLUG_RE = /^[a-z0-9]+(-[a-z0-9]+)*$/;

const slugSchema = z
  .string()
  .trim()
  .min(1, "Slug fehlt")
  .max(120, "Slug ist zu lang")
  .regex(SLUG_RE, "Slug darf nur Kleinbuchstaben, Ziffern und Bindestriche enthalten.");

export type PublicTtpaPage = {
  title: string;
  slug: string;
  content_md: string;
  updated_at: string;
  published_at: string | null;
};

// Ticket T1: Öffentlich wird ausschließlich der freigegebene Stand ausgeliefert
// (published_*). Entwürfe und Entwurfsänderungen an veröffentlichten Seiten
// bleiben unsichtbar, bis sie erneut freigegeben werden.
export const getPublicTtpaPage = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => z.object({ slug: slugSchema }).parse(input))
  .handler(async ({ data }): Promise<PublicTtpaPage | null> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: row, error } = await supabaseAdmin
      .from("ttpa_pages")
      .select("published_title, published_slug, published_content_md, published_at, updated_at")
      .eq("published_slug", data.slug)
      .eq("status", "published")
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!row) return null;
    const r = row as {
      published_title: string | null;
      published_slug: string | null;
      published_content_md: string | null;
      published_at: string | null;
      updated_at: string;
    };
    if (!r.published_slug) return null;
    return {
      title: r.published_title ?? "",
      slug: r.published_slug,
      content_md: r.published_content_md ?? "",
      updated_at: r.published_at ?? r.updated_at,
      published_at: r.published_at,
    };
  });

export const ttpaPageQueryOptions = (slug: string) =>
  queryOptions({
    queryKey: ["ttpa-page", slug],
    queryFn: () => getPublicTtpaPage({ data: { slug } }),
  });
